import { useEffect, useState } from "react";
import { Laptop, Moon, Sun } from "lucide-react";

type ThemeMode = "auto" | "dark" | "light";

const nextMode: Record<ThemeMode, ThemeMode> = { auto: "dark", dark: "light", light: "auto" };
const labels: Record<ThemeMode, string> = { auto: "Theme: automatic", dark: "Theme: dark", light: "Theme: light" };

function applyTheme(mode: ThemeMode) {
  const dark = mode === "dark" || (mode === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.dataset.themeMode = mode;
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
}

export function ThemeToggle({ compact = false, scope = "studio" }: { compact?: boolean; scope?: "studio" | "proposal" }) {
  const storageKey = scope === "proposal" ? "lvthn-proposal-theme" : "lvthn-theme";
  const [mode, setMode] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(storageKey);
    return stored === "dark" || stored === "light" || stored === "auto" ? stored : scope === "proposal" ? "light" : "auto";
  });

  useEffect(() => {
    const syncToggle = (event: Event) => {
      const detail = (event as CustomEvent<{ storageKey: string; mode: ThemeMode }>).detail;
      if (detail?.storageKey === storageKey) setMode(detail.mode);
    };
    window.addEventListener("lvthn-theme-change", syncToggle);
    return () => window.removeEventListener("lvthn-theme-change", syncToggle);
  }, [storageKey]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => applyTheme(mode);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [mode]);

  const cycle = () => {
    const next = nextMode[mode];
    localStorage.setItem(storageKey, next);
    applyTheme(next);
    setMode(next);
    window.dispatchEvent(new CustomEvent("lvthn-theme-change", { detail: { storageKey, mode: next } }));
  };

  const Icon = mode === "auto" ? Laptop : mode === "dark" ? Moon : Sun;
  return <button className={`theme-toggle ${compact ? "theme-toggle--compact" : ""}`} type="button" onClick={cycle} aria-label={`${labels[mode]}. Change theme.`} title={`${labels[mode]} — click to change`}><Icon /><span>{mode === "auto" ? "Auto" : mode === "dark" ? "Dark" : "Light"}</span></button>;
}
